import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Dropdown } from 'antd';
import { selector as defaultLandingPageSelector } from '@store/defaultLandingPage';
import commonSelector, { isAdminRoleSelector, isStaffRoleSelector } from '@store/common/selectors';
import {
  setDefaultLandingPage,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptOkFn,
  showPrompt,
} from '@store/defaultLandingPage/actions';
import { setActiveDashboardId } from '@store/dashboards/ui/actions';
import getActualShareLink from '@components/common/Sharing/SharingModal/SharingContent/getActualShareLink';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import featureNames from '@utils/featureToggle/constants';
import { FormActionContext } from '../../../form/context';
import ToggleIcon from '@icons/common/toggle.svg';
import { ToggleButton } from '@src/components/common/Utils/Buttons';

const Wrapper = styled.div`
  position: absolute;
  right: 24px;
  top: 24px;
  display: inline-flex;
  align-items: center;

  button {
    margin-right: 16px;
  }
`;

const PageButtons = () => {
  const {
    actions: { onCreate },
  } = React.useContext(FormActionContext);
  const dispatch = useDispatch();
  const { features, profiles } = useSelector(commonSelector);
  const isAdmin = useSelector(isAdminRoleSelector);
  const isStaff = useSelector(isStaffRoleSelector);
  const canCreateAlert = isAdmin || isStaff;
  const cid = profiles.filter((p) => p.is_selected)[0].customer_id;
  const defaultLandingPage = useSelector(defaultLandingPageSelector);
  const setLandingPageEnabled = features[featureNames.DEFAULT_LANDING_PAGE] || true;
  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);

  const onSetDefaultLandingApply = () => {
    const u = getActualShareLink(pathname, cid);
    dispatch(
      setDefaultLandingPage({
        url: u,
        name: 'Alert',
        path: pathname,
      }),
    );
  };

  const hasDefaultLandingPage = defaultLandingPage.url.length > 0;
  const dlpContent = hasDefaultLandingPage
    ? 'Are you sure you want to change your default dashboard?'
    : 'Are you sure you want to set your default dashboard?';
  const dlpOkFn = () => onSetDefaultLandingApply();
  const onSetDefaultLanding = () => {
    dispatch(setActiveDashboardId(null));
    dispatch(setDefaultLandingPagePromptNeedInput(false));
    dispatch(setDefaultLandingPagePromptTitle('Make default'));
    dispatch(setDefaultLandingPagePromptContent(dlpContent));
    dispatch(setDefaultLandingPagePromptOkFn(dlpOkFn));
    dispatch(showPrompt());
  };

  const onApply = ({ key: kebabId }: any) => {
    switch (kebabId) {
      case 'default': {
        onSetDefaultLanding();
        break;
      }
    }
  };

  return (
    <Wrapper>
      {canCreateAlert && (
        <Button type="primary" onClick={onCreate}>
          Create Alert
        </Button>
      )}
      {setLandingPageEnabled && (
        <Dropdown
          menu={{
            items: [{ key: 'default', label: 'Make Default', disabled: isDefaultLandingPage }],
            onClick: onApply,
          }}
          trigger={['click']}
        >
          <ToggleButton icon={<ToggleIcon />} />
        </Dropdown>
      )}
      <DefaultLandingPrompt />
    </Wrapper>
  );
};

export default PageButtons;
